import { useEffect, useRef } from "react";
import type { StreamItem } from "../../types/meeting.types";
import RenderParticipantIcons from "./RenderParticipantIcons";
import clsx from "clsx";
import { useMeetingStore } from "../../store/meeting.store";
import avatarImg from "../../../src/assets/judge.jpg";
import silhouette from "../../assets/silhouette.jpg";
import { RaisedHand } from "../Icons";

interface Props {
  handRaised?: boolean;
  stream: MediaStream;
  peer: StreamItem;
  videoRefs: React.MutableRefObject<HTMLVideoElement[]>;
  index: number;
  activeDeviceSelector: string | null;
  setActiveDeviceSelector: (key: string | null) => void;
}

export function VideoTile({
  stream,
  peer,
  videoRefs,
  index,
  activeDeviceSelector,
  setActiveDeviceSelector,
  handRaised = false,
}: Props) {
  const isHost = useMeetingStore((s) => s.isHost());

  const videoRef = useRef<HTMLVideoElement>(null);

  /* ---------- Attach stream once ---------- */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;

    if (video.srcObject !== stream) {
      video.srcObject = stream;
      // Ensure playback starts after srcObject is set
      video.play().catch(() => {});
    }
  }, [stream]);

  /* ---------- Freeze / resume ---------- */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (peer.frozen) {
      video.pause();
    } else {
      if (video.srcObject && video.readyState >= 2) {
        video.play().catch(() => {});
      }
    }
  }, [peer.frozen]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!peer.videoPaused) {
      // Camera turned back on — ensure srcObject is set and playing
      if (video.srcObject !== stream) {
        video.srcObject = stream;
      }
      if (!peer.frozen) {
        video.play().catch(() => {});
      }
    }
    // When videoPaused=true, we don't pause the video element —
    // we just hide it with CSS. The stream keeps flowing to canvas.
    // This is intentional: canvas drawFrame checks stream.videoPaused
    // and draws black rect instead, so the recording shows black
    // but the video element stays alive for when camera turns back on.
  }, [peer.videoPaused]);

  return (
    <div
      style={{
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
        WebkitBackfaceVisibility: "hidden",
      }}
      className={clsx(
        handRaised
          ? "border-amber-400 border-3"
          : "border-2 hover:border-blue-500",
        "video-tile",
        peer.speaking && !handRaised && "speaking",
        "relative h-full min-h-0 overflow-hidden rounded-lg transition-all duration-300",
      )}
    >
      {/* ✅ FIX 6: ALWAYS render the video element — NEVER unmount it.
          Show/hide with CSS visibility + absolute positioning.
          This ensures:
          - srcObject persists across pause/resume
          - videoRefs[index] always has a valid element
          - canvas drawFrame always has a video source
          - No re-mount → no srcObject loss → no blank feed */}
      <video
        ref={(el) => {
          if (!el) {
            if (videoRefs?.current) {
              delete videoRefs.current[index];
            }
            return;
          }

          videoRef.current = el;
          if (videoRefs?.current) videoRefs.current[index] = el;
          if (el.srcObject !== stream && stream) {
            el.srcObject = stream;
            el.play().catch(() => {});
          }
        }}
        muted={Boolean(peer.localMute) || Boolean(peer.audioPaused)}
        autoPlay
        playsInline
        style={{
          transform: peer?.userId === "teams" ? "scaleX(1)" : "scaleX(-1)",
          transition: "filter 0.3s ease", // ← update
          filter: peer.blurred ? "blur(12px)" : "none", // ← ADD
          opacity: peer.videoPaused ? 0 : 1,
          position: "absolute",
          inset: 0,
          objectFit: "cover",
          pointerEvents: peer.videoPaused ? "none" : "auto",
          width: "100%",
          height: "100%",
        }}
        className="object-cover"
      />

      {/* Avatar shown when camera is off — layered on top of hidden video */}
      {peer.videoPaused && (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900 absolute inset-0">
          <div className="w-24 h-24 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold">
            {peer.role === "Judge" ? (
              <img
                src={avatarImg}
                alt={peer.name}
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <img
                src={silhouette}
                alt={`${peer.name.charAt(0).toUpperCase()}-img`}
                className="w-24 h-24 rounded-full object-cover"
              />
            )}
          </div>
        </div>
      )}
      {/* Name label */}
      <div className="absolute bottom-1 left-1 right-3 z-10">
        <div className="bg-black/10 backdrop-blur-sm rounded-md px-3 py-1.5 max-w-fit ">
          <span className="text-white text-sm md:text-base font-medium truncate block flex items-center gap-1">
            {handRaised && (
              <span title="Hand raised" role="img" aria-label="Hand raised">
                <RaisedHand className="h-5 w-6" aria-hidden="true" />
              </span>
            )}{" "}
            {peer.name}
          </span>
        </div>
      </div>

      {/* Volume bar */}
      <div className="volume-bar">
        <div
          className="volume-level"
          style={{ width: `${peer.volume || 0}%` }}
        />
      </div>

      {isHost && (
        <RenderParticipantIcons
          peer={peer}
          activeDeviceSelector={activeDeviceSelector}
          setActiveDeviceSelector={setActiveDeviceSelector}
        />
      )}
    </div>
  );
}
